import {
  Color as ColorEnum,
  nodeToList,
  Result,
  Status,
} from "@chessalpha/core";
import { Box, Button, List, Typography } from "@mui/material";
import { Card, CardActions, CardDescription } from "@ssf/components";
import type { gameSchema } from "@ssf/play-common";
import { challengeSchema, z } from "@ssf/play-common";
import {
  challengesRef,
  gamesRef,
  nodesRef,
  orderByChild,
  query,
  ref,
  remove,
  serverTimeOffsetRef,
  startAt,
  userToPlayer,
  useValue,
} from "@ssf/play-components";
import type { DatabaseReference, DataSnapshot } from "firebase/database";
import { runTransaction, serverTimestamp } from "firebase/database";
import { uniqBy } from "lodash";
import { Fish, SwordCross } from "mdi-material-ui";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";

import { generatePath, usePlayer, useRoom } from "../lib/index.js";
import { ChallengeDialog, ChallengeItem } from "./index.js";

const challengesSchema = z
  .record(challengeSchema)
  .or(z.null().transform(() => ({})));

const creatingGames = new Set<string>();

const createIfMissing = (path: string[], value: unknown) =>
  runTransaction(
    ref(...path),
    (currentValue) => (currentValue === null ? value : undefined),
    { applyLocally: false },
  );

const removeChallengeIfOwned = (roomKey: string, gid: string, uid: string) =>
  runTransaction(
    ref(challengesRef, roomKey, gid),
    (currentValue) => (currentValue?.user?.uid === uid ? null : undefined),
    { applyLocally: false },
  );

export const ChallengesCard = ({ roomKey = "lobby" }: { roomKey?: string }) => {
  const { t } = useTranslation("ChallengesCard");
  const [newChallengeDialog, setNewChallengeDialog] = useState(false);
  const { data: serverTimeOffset } = useValue(serverTimeOffsetRef)<number>();
  const snackbar = useSnackbar();
  const navigate = useNavigate();

  const { data: room } = useRoom(roomKey);
  const { player } = usePlayer();
  const { data: challengesObj } = useValue(
    challengesRef,
    roomKey,
  )({
    query: useCallback(
      (ref: DatabaseReference) => {
        const ttl = Date.now() - 30 * 60 * 1000 + (serverTimeOffset || 0);
        return query(ref, orderByChild("createdAt"), startAt(ttl));
      },
      [serverTimeOffset],
    ),
    disabled: serverTimeOffset === undefined,
    schema: challengesSchema,
    onSuccess: useCallback(
      (snapshot: DataSnapshot) => {
        const challengesObj = challengesSchema.parse(snapshot.val());
        for (const [gid, challenge] of Object.entries(challengesObj)) {
          const opponent = challenge.accepted;
          const creationKey = `${roomKey}/${gid}`;
          if (
            player &&
            challenge.user.uid === player.uid &&
            opponent &&
            room &&
            !creatingGames.has(creationKey)
          ) {
            creatingGames.add(creationKey);
            const color =
              challenge.color === ColorEnum.white
                ? 1
                : challenge.color === ColorEnum.black
                  ? 0
                  : Math.round(Math.random());
            const currentPlayer = {
              uid: player.uid,
              displayName: player.name,
            };
            const game: z.infer<typeof gameSchema> = {
              white: userToPlayer(color ? currentPlayer : opponent),
              black: userToPlayer(color ? opponent : currentPlayer),
              status: Status.ACCEPTED,
              result: Result.ONGOING,
              timeControl: {
                white: [challenge.timeControl],
                black: [challenge.timeControl],
              },
              createdBy: player.uid,
              createdAt: serverTimestamp() as unknown as number,
              updatedAt: serverTimestamp() as unknown as number,
              autoStart: room.autoStart || false,
              antiCheatCheck: room.antiCheatCheck || false,
            };
            const nodes = nodeToList({
              position: room.startPosition ?? "start",
              start: true,
              timestamp: serverTimestamp() as unknown as number,
              whiteTime: challenge.timeControl.limit,
              blackTime: challenge.timeControl.limit,
              next: [],
            });
            void Promise.all([
              createIfMissing([gamesRef, roomKey, gid], game),
              createIfMissing([nodesRef, roomKey, gid], nodes),
            ])
              .then(() => removeChallengeIfOwned(roomKey, gid, player.uid))
              .then(() => {
                navigate(generatePath("game", { gid, room: roomKey }));
              })
              .catch((error) => {
                console.error(error);
                snackbar.enqueueSnackbar("Error while creating game");
              })
              .finally(() => {
                creatingGames.delete(creationKey);
              });
          }
        }
        return challengesObj;
      },
      [navigate, player, room, roomKey, snackbar],
    ),
    onError(error, snapshot, currentData) {
      try {
        if (error instanceof z.ZodError) {
          const records = z.record(z.unknown()).parse(snapshot.val());
          const validRecords = Object.fromEntries(
            Object.entries(records).filter(
              ([, value]) => challengeSchema.safeParse(value).success,
            ),
          ) as z.infer<typeof challengesSchema>;
          return validRecords;
        }
      } catch (_error) {}
      snackbar?.enqueueSnackbar("Error while loading challenges");
      return currentData;
    },
  });

  const challenges = useMemo(() => {
    return challengesObj
      ? uniqBy(
          Object.entries(challengesObj).reverse(),
          ([, { user }]) => user.uid,
        )
      : [];
  }, [challengesObj]);

  // Remove challenges when unmounting
  const reactChallengesRef = useRef<typeof challenges>(undefined);
  const playerUidRef = useRef(player?.uid);
  useEffect(() => {
    reactChallengesRef.current = challenges;
    playerUidRef.current = player?.uid;
  }, [challenges, player?.uid]);
  useEffect(() => {
    return () => {
      reactChallengesRef.current?.forEach(([key, challenge]) => {
        if (challenge.user.uid === playerUidRef.current) {
          remove(challengesRef, roomKey, key);
        }
      });
    };
  }, [roomKey]);

  return (
    <>
      <Card title={t("title")} icon={<SwordCross />} disablePadding>
        <CardDescription sx={{ pt: 2, px: 2, mb: 0 }}>
          {t("description")}
        </CardDescription>
        <List
          sx={{
            height: ({ spacing }) => spacing(46),
            overflow: "auto",
            ...(!challenges.length && {
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }),
          }}
        >
          {challenges.length ? (
            challenges.map(([key, challenge]) => (
              <ChallengeItem
                key={key}
                challenge={challenge}
                roomKey={roomKey}
                gid={key}
              />
            ))
          ) : (
            <Typography component="li" variant="body2" align="center">
              {t("empty")}
            </Typography>
          )}
        </List>
      </Card>
      <CardActions>
        <Button
          variant="contained"
          disabled={!player}
          onClick={() => setNewChallengeDialog(true)}
        >
          {t("action")}
        </Button>
        <Box sx={{ flex: "1 0 auto" }} />
        <Button
          component={Link}
          to={generatePath("engine", {})}
          variant="contained"
          color="info"
          startIcon={<Fish />}
        >
          {t("playEngine")}
        </Button>
      </CardActions>

      <ChallengeDialog
        roomKey={roomKey}
        challengesObj={challengesObj}
        open={newChallengeDialog}
        onClose={() => setNewChallengeDialog(false)}
      />
    </>
  );
};
